import axios from 'axios';
import apiClient from '@/services/apiClient';

const S = '/staff-attendance';
const KIOSK_KEY = 'staff_att_kiosk';

export const kioskConfig = {
  get: () => { try { return JSON.parse(localStorage.getItem(KIOSK_KEY) || 'null'); } catch { return null; } },
  set: (cfg) => localStorage.setItem(KIOSK_KEY, JSON.stringify(cfg)),
  clear: () => localStorage.removeItem(KIOSK_KEY),
};

const kioskHttp = () => {
  const cfg = kioskConfig.get() || {};
  return axios.create({
    baseURL: `${process.env.REACT_APP_BACKEND_URL}/api${S}/kiosk`,
    timeout: 60000,
    headers: { 'Content-Type': 'application/json', 'X-Kiosk-Token': cfg.token || '', 'X-Site-Id': cfg.site_id || 'salem' },
  });
};

export const kioskApi = {
  me: () => kioskHttp().get('/me').then((r) => r.data),
  lookup: (staff_id) => kioskHttp().post('/lookup', { staff_id }).then((r) => r.data),
  punch: (body) => kioskHttp().post('/punch', body).then((r) => r.data),
};

export const staffAttApi = {
  myToday: () => apiClient.get(`${S}/me/today`).then((r) => r.data),
  myPunch: (body) => apiClient.post(`${S}/me/punch`, body).then((r) => r.data),
  queue: (params) => apiClient.get(`${S}/queue`, { params }).then((r) => r.data),
  queueCount: () => apiClient.get(`${S}/queue/count`).then((r) => r.data),
  approve: (id) => apiClient.post(`${S}/punches/${id}/approve`).then((r) => r.data),
  reject: (id, reason) => apiClient.post(`${S}/punches/${id}/reject`, { reason }).then((r) => r.data),
  approveMatched: (body) => apiClient.post(`${S}/queue/approve-matched`, body).then((r) => r.data),
  useAsReference: (id) => apiClient.post(`${S}/punches/${id}/use-as-reference`, { consent: true }).then((r) => r.data),
  references: (params) => apiClient.get(`${S}/references`, { params }).then((r) => r.data),
  reference: (sid) => apiClient.get(`${S}/references/${encodeURIComponent(sid)}`).then((r) => r.data),
  setReference: (sid, photo) => apiClient.post(`${S}/references/${encodeURIComponent(sid)}`, { photo, consent: true }).then((r) => r.data),
  daily: (params) => apiClient.get(`${S}/daily`, { params }).then((r) => r.data),
  foodAllowance: (params) => apiClient.get(`${S}/food-allowance`, { params }).then((r) => r.data),
  foodAllowancePdf: (params) => apiClient.get(`${S}/food-allowance/pdf`, { params, responseType: 'blob' }).then((r) => r.data),
  headcount: (params) => apiClient.get(`${S}/headcount`, { params }).then((r) => r.data),
  manpower: (date, collar) => apiClient.get(`${S}/manpower`, { params: { date, collar: collar || undefined } }).then((r) => r.data),
  raiseRequest: (body) => apiClient.post(`${S}/manpower/requests`, body).then((r) => r.data),
  sendToRequest: (rid, staffIds) => apiClient.post(`${S}/manpower/requests/${rid}/send`, { staff_ids: staffIds }).then((r) => r.data),
  deployDirect: (body) => apiClient.post(`${S}/manpower/deployments`, body).then((r) => r.data),
  decideDeployment: (did, action) => apiClient.post(`${S}/manpower/deployments/${did}/${action}`).then((r) => r.data),
  agencySupply: (rid, body) => apiClient.post(`${S}/manpower/requests/${rid}/agency`, body).then((r) => r.data),
  closeRequest: (rid) => apiClient.post(`${S}/manpower/requests/${rid}/close`).then((r) => r.data),
  sendable: (date) => apiClient.get(`${S}/manpower/sendable`, { params: { date } }).then((r) => r.data.staff),
  transferStaff: (sid, body) => apiClient.post(`${S}/staff/${encodeURIComponent(sid)}/transfer`, body).then((r) => r.data),
  mandayReport: (params) => apiClient.get(`${S}/manday-report`, { params }).then((r) => r.data),
  contractPayroll: (params) => apiClient.get(`${S}/contract-payroll`, { params }).then((r) => r.data),
  setPayDecision: (body) => apiClient.put(`${S}/contract-payroll/decision`, body).then((r) => r.data),
  regularPayroll: (params) => apiClient.get(`${S}/regular-payroll`, { params }).then((r) => r.data),
  setRegularPayDecision: (body) => apiClient.put(`${S}/regular-payroll/decision`, body).then((r) => r.data),
  regularPayRules: () => apiClient.get(`${S}/regular-payroll/rules`).then((r) => r.data),
  saveRegularPayRules: (body) => apiClient.put(`${S}/regular-payroll/rules`, body).then((r) => r.data),
  setWorkRole: (id, work_role) => apiClient.post(`${S}/punches/${id}/work-role`, { work_role }).then((r) => r.data),
  contractAgencies: () => apiClient.get(`${S}/contract-agencies`).then((r) => r.data),
  saveContractAgencies: (agencies) => apiClient.put(`${S}/contract-agencies`, { agencies }).then((r) => r.data),
  monthly: (params) => apiClient.get(`${S}/monthly`, { params }).then((r) => r.data),
  missedCheckouts: (params) => apiClient.get(`${S}/missed-checkouts`, { params }).then((r) => r.data),
  fixMissed: (id, body) => apiClient.post(`${S}/missed-checkouts/${id}/fix`, body).then((r) => r.data),
  esslUpload: (file, device) => {
    const fd = new FormData();
    fd.append('file', file);
    if (device) fd.append('device', device);
    return apiClient.post(`${S}/essl/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 }).then((r) => r.data);
  },
  esslUploads: () => apiClient.get(`${S}/essl/uploads`).then((r) => r.data.uploads),
  pastTemplate: (from_month, to_month) => apiClient.get(`${S}/past/template`, { params: { from_month, to_month }, responseType: 'blob', timeout: 120000 }).then((r) => r.data),
  pastUpload: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return apiClient.post(`${S}/past/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 }).then((r) => r.data);
  },
  pastUploads: () => apiClient.get(`${S}/past/uploads`).then((r) => r.data.uploads),
  esslReconcile: (params) => apiClient.get(`${S}/essl/reconcile`, { params }).then((r) => r.data),
  esslApply: (staff_id, date) => apiClient.post(`${S}/essl/apply`, { staff_id, date }).then((r) => r.data),
  esslBackfill: (body) => apiClient.post(`${S}/essl/backfill`, body, { timeout: 180000 }).then((r) => r.data),
  settings: () => apiClient.get(`${S}/settings`).then((r) => r.data),
  saveSettings: (body) => apiClient.put(`${S}/settings`, body).then((r) => r.data),
  specialDays: (year) => apiClient.get(`${S}/special-days`, { params: year ? { year } : {} }).then((r) => r.data),
  markSpecialDay: (date, body) => apiClient.put(`${S}/special-days/${date}`, body).then((r) => r.data),
  unmarkSpecialDay: (date) => apiClient.delete(`${S}/special-days/${date}`).then((r) => r.data),
  sections: () => apiClient.get(`${S}/sections`).then((r) => r.data),
  kiosks: () => apiClient.get(`${S}/kiosks`).then((r) => r.data.kiosks),
  createKiosk: (body) => apiClient.post(`${S}/kiosks`, body).then((r) => r.data),
  removeKiosk: (id) => apiClient.delete(`${S}/kiosks/${id}`).then((r) => r.data),
};

export const errMsg = (e, fb = 'Something went wrong') => {
  const d = e?.response?.data?.detail;
  return typeof d === 'string' ? d : fb;
};

export const fmtTime = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' }); } catch { return '—'; }
};

export const todayIST = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
export const WORK_ROLES = { truck_loadman: 'Truck Loadman', unloading_loadman: 'Unloading Loadman', piece_rate: 'Piece Rate' };
export const kindLabel = (kind, sessionCount = 0) => (kind === 'in' ? (sessionCount > 0 ? 'Check In again' : 'Check In') : 'Check Out');
export const withPool = (list) => [...new Set(list.some((s) => /^contract(or|ors)?\s*(labou?r|workers)?\s*pool$/i.test(String(s).trim())) ? list : [...list, 'Contract Pool'])];

